/*
  Container helper functions for the module.
  These are meant to remain private and not exported outside of this module.

  The constants and logic in the transform functions was directly ported from the cart BFF.
*/

import { ObjectValues } from '@app/types';
import { toGqlShippingMethod } from '@app/types/cart/ShippingMethod';
import {
  Cart as MonolithCart,
  Message as MonolithCartMessage,
  AppliedCoupon as MonolithCoupon,
  CouponType as MonolithCouponType,
  Item as MonolithLineItem,
  Severity as MonolithMessageSeverity,
  Type as MonolithMessageType,
  EstimatedShipping as MonolithShipping,
  ShippingPreference as MonolithShippingPreference,
  Summary as MonolithSummary,
  Totals as MonolithTotals,
} from '@app/types/monolith/cart';
import {
  ApplyCouponAPIError,
  AssignedCouponsAPIResponse,
  RemoveCouponAPIError,
  UpdateLineItemAPIError,
  UpdateLineItemErrorCodes,
} from '@app/types/monolith/types';
import {
  AppliedCoupon,
  AssignedCoupons,
  Cart,
  CartUserError,
  CouponType,
  Details,
  EstimatedShipping,
  LineItem,
  Message,
  MessageSeverity,
  MessageType,
  Price,
  PriceWithTax,
  Shipping,
  ShippingMethod,
  Summary,
  Tax,
  Totals,
  Work,
} from './types';

const ERR_CODE_UNHANDLED = 1000;
const ERR_CODE_INVALID = 1100;
const ERR_CODE_NOT_FOUND = 1200;

const DEFAULT_MESSAGE_APPLIED = false;
const DEFAULT_SHIPPING_METHOD: MonolithShippingPreference = 'standard';
const NONSALEABLE_PRODUCT_REMOVAL_URL =
  'https://help.redbubble.com/hc/articles/360036855832';

export const transformApplyCouponError = ({
  error,
  error_code,
  error_params,
}: ApplyCouponAPIError): CartUserError[] => {
  return [
    {
      __typename: 'CartUserError',
      code: error_code || 'unhandled',
      field: ['cart', 'coupon'],
      message: error,
      messageParams: error_params,
    },
  ];
};

export const transformRemoveCouponError = ({
  error_code,
  error_params,
}: RemoveCouponAPIError): CartUserError[] => {
  return [
    {
      __typename: 'CartUserError',
      code: error_code || 'unhandled',
      field: ['cart', 'coupon'],
      messageParams: error_params,
    },
  ];
};

export const transformCart = (cart: MonolithCart): Cart => {
  const {
    summary,
    messages,
    currency,
    items,
    total_items_count: count,
    digital_products_only,
    applied_coupon,
    expired_products_removed,
    nonsaleable_products_removed,
  } = cart;
  const { show_tax_separately } = summary;

  return {
    __typename: 'Cart',
    appliedCoupon: applied_coupon && transformAppliedCoupon(applied_coupon),
    summary: transformSummary(summary, {
      currency,
      count,
      digital_products_only,
    }),
    lineItems: items.map((item) =>
      transformLineItem(item, currency, show_tax_separately)
    ),
    messages: transformMessages(
      messages,
      expired_products_removed,
      nonsaleable_products_removed
    ),
  };
};

export const transformCartUserErrors = (
  error: UpdateLineItemAPIError
): CartUserError => {
  const defaultFields: string[] = ['cart', 'lineItem'];
  const err = error.error;
  // The 'cart_item_quantity_invalid' error comes from the Monolith model validation rules "app/models/cart_line_item.rb"
  // and is transformed into structured errors in "app/controllers/cart/items_controller.rb".
  // The "cart_item_not_found" message is from an error in "app/controllers/cart/items_controller.rb".
  // The UI maps the return types to actual copies in "app/pages/cart/errorDict.tsx".

  if (err.error_code === UpdateLineItemErrorCodes.QuantityInvalid) {
    return {
      __typename: 'CartUserError',
      code: ERR_CODE_INVALID,
      field: defaultFields.concat('quantity'),
      messageParams: err.error_params,
    };
  }

  if (err.error_code === UpdateLineItemErrorCodes.ItemNotFound) {
    return {
      __typename: 'CartUserError',
      code: ERR_CODE_NOT_FOUND,
      field: defaultFields,
    };
  }

  return {
    __typename: 'CartUserError',
    code: ERR_CODE_UNHANDLED,
    field: defaultFields,
  };
};

export const transformAssignedCoupons = (
  assignedCouponsMonolith: AssignedCouponsAPIResponse
): AssignedCoupons => {
  return {
    __typename: 'AssignedCoupons',
    autoApplied: assignedCouponsMonolith.coupon_applied,
    coupons: assignedCouponsMonolith.assigned_coupons.map((coupon) => ({
      __typename: 'AssignedCoupon',
      code: coupon.code,
      displayName: coupon.display_name,
      expiryDate: coupon.expiry_date,
      type: mapCouponType(coupon.type),
    })),
  };
};

const makePrice = (amount: number, currency: string): Price => {
  return { __typename: 'Price', amount, currency };
};

const makePriceWithTax = ({
  amountExTax,
  taxAmount,
  currency,
  amountIncTax,
  type,
}: {
  amountExTax: number;
  taxAmount: number;
  currency: string;
  amountIncTax: number;
  type: string;
}): PriceWithTax => {
  return {
    __typename: 'PriceWithTax',
    amountExTax,
    price: {
      __typename: 'Price',
      amount: amountIncTax,
      currency,
    },
    tax: {
      __typename: 'Tax',
      amount: taxAmount,
      currency,
      type,
    },
  };
};

const makeTax = (amount: number, currency: string, type: string): Tax => {
  return { __typename: 'Tax', amount, currency, type };
};

const mapCouponType = (monolithCouponType: MonolithCouponType) => {
  const mappedCouponType = {
    AmountCoupon: CouponType.Amount,
    FreeSampleCoupon: CouponType.FreeSample,
    FreeShippingCoupon: CouponType.FreeShipping,
    GiftCertificateCoupon: CouponType.GiftCertificate,
    PercentOffCoupon: CouponType.PercentOff,
    VariablePercentOffCoupon: CouponType.VariablePercentOff,
  }[monolithCouponType];

  return mappedCouponType || monolithCouponType;
};

const transformAppliedCoupon = (
  coupon: MonolithCoupon
): AppliedCoupon | null => {
  if (coupon.is_expired) return null;

  const {
    code,
    display_name: displayName,
    expiry_date: expiryDate,
    sub_discount,
    type,
  } = coupon;
  const amount = parseFloat(sub_discount.amount);
  const couponType = mapCouponType(type);

  return {
    __typename: 'AppliedCoupon',
    code,
    discount: makePrice(amount, sub_discount.currency.iso),
    displayName,
    expiryDate,
    type: couponType,
  };
};

const transformLineItemDetails = (
  lineItem: MonolithLineItem,
  currency: string,
  showTaxSeparately: boolean
): Details => {
  const {
    blank_item_id: blankItemId,
    details: { options },
    image_url: imageUrl,
    inventory_item_id: inventoryItemId,
    marketing_product_type_id: marketingProductTypeId,
    marketing_sku: marketingSku,
    product_type_id: productTypeId,
    item_price,
    item_price_ex_tax,
    type,
    work_url: productUrl,
  } = lineItem;

  return {
    __typename: 'Details',
    blankItemId,
    imageUrl,
    inventoryItemId,
    marketingProductTypeId,
    marketingSku,
    options,
    price: makePrice(
      showTaxSeparately ? item_price_ex_tax : item_price,
      currency
    ),
    productTypeId,
    productUrl,
    type,
  };
};

const transformLineItem = (
  lineItem: MonolithLineItem,
  currency: string,
  showTaxSeparately: boolean
): LineItem => {
  const {
    total_price,
    total_price_ex_tax,
    quantity,
    id,
    is_digital: isDigital,
    ga_code: gaCode,
    fixed_quantity,
    total_shipping_price,
    undiscounted_total_price,
    undiscounted_total_price_ex_tax,
    artist_name: artistName,
    work_id,
    work_title: title,
  } = lineItem;

  const work: Work = {
    __typename: 'Work',
    title,
    artistName,
    id: (work_id || 0).toString(),
  };

  return {
    __typename: 'LineItem',
    id: id.toString(),
    isDigital,
    gaCode,
    quantity,
    isQuantityEditable: !fixed_quantity,
    total: makePrice(
      showTaxSeparately ? total_price_ex_tax : total_price,
      currency
    ),
    undiscountedTotal: makePrice(
      showTaxSeparately
        ? undiscounted_total_price_ex_tax
        : undiscounted_total_price,
      currency
    ),
    totalShipping: makePrice(total_shipping_price, currency),
    work,
    details: transformLineItemDetails(lineItem, currency, showTaxSeparately),
  };
};

const transformMessages = (
  messages: MonolithCartMessage[],
  expired_products_removed: number[],
  nonsaleable_products_removal: number[]
) => {
  // NOTE(cy) 20251209:
  // Filter out existing nonsaleable/expired removal messages since they're inconsistently
  // included across endpoints (e.g., GET /fep/api/cart-monolith includes them,
  // POST /fep/api/shipping-preference doesn't).
  // The addition of nonsable/expired messages are done in the NextJS server.
  const transformed = messages
    .filter(
      (m) =>
        m.type !== 'nonsaleable_product_removal' &&
        m.type !== 'expired_product_removal'
    )
    .map(transformMessage);

  if (expired_products_removed.length > 0)
    transformed.push({
      __typename: 'Message',
      type: MessageType.ExpiredProductRemoval,
      severity: MessageSeverity.Error,
      productName: null,
      actionUrl: null,
      applied: false,
      text: null,
    });

  if (nonsaleable_products_removal.length > 0)
    transformed.push({
      __typename: 'Message',
      type: MessageType.NonsaleableProductRemoval,
      severity: MessageSeverity.Error,
      productName: null,
      actionUrl: NONSALEABLE_PRODUCT_REMOVAL_URL,
      applied: false,
      text: null,
    });

  return transformed;
};

const transformMessage = (message: MonolithCartMessage): Message => {
  const {
    product_name: productName,
    text,
    severity,
    action_url: actionUrl,
    type,
    applied,
  } = message;

  return {
    __typename: 'Message',
    text,
    actionUrl: actionUrl || null,
    productName: productName || null,
    severity: transformMessageSeverity(severity),
    type: transformMessageType(type),
    applied: applied !== undefined ? applied : DEFAULT_MESSAGE_APPLIED,
  };
};

const transformMessageSeverity = (
  severity: ObjectValues<typeof MonolithMessageSeverity>
): MessageSeverity => {
  switch (severity) {
    case MonolithMessageSeverity.Info:
      return MessageSeverity.Information;
    case MonolithMessageSeverity.Success:
      return MessageSeverity.Success;
    case MonolithMessageSeverity.Warning:
      return MessageSeverity.Warning;
    case MonolithMessageSeverity.Error:
      return MessageSeverity.Error;
    default:
      // TODO: We should stop throwing and recover gracefully. Alog this so we are aware of missing mappings.
      throw new Error('unknown severity', severity);
  }
};

const transformMessageType = (
  type: ObjectValues<typeof MonolithMessageType>
): MessageType => {
  switch (type) {
    case MonolithMessageType.ProductWarning:
      return MessageType.ProductWarning;
    case MonolithMessageType.DiscountSitewide:
      return MessageType.DiscountSitewide;
    case MonolithMessageType.DiscountVolume:
      return MessageType.DiscountVolume;
    case MonolithMessageType.DiscountShipping:
      return MessageType.DiscountShipping;
    case MonolithMessageType.FreeShippingOnProductApplied:
      return MessageType.FreeShippingOnProductApplied;
    case MonolithMessageType.FlatRateShippingApplied:
      return MessageType.FlatRateShippingApplied;
    case MonolithMessageType.NonsaleableProductRemoval:
      return MessageType.NonsaleableProductRemoval;
    case MonolithMessageType.ExpiredProductRemoval:
      return MessageType.ExpiredProductRemoval;
    default:
      // TODO: We should stop throwing and recover gracefully. Also this is so we are aware of missing mappings.
      throw new Error('unknown message Type', type);
  }
};

const transformShipping = (
  shipping: MonolithShipping,
  totals: MonolithTotals,
  currency: string
): Shipping => {
  const {
    country_name: countryName,
    country_code: countryCode,
    preference,
    express,
    express_ex_tax: expressExTax,
    standard,
    standard_ex_tax: standardExTax,
    allow_express,
  } = shipping;

  const method = toGqlShippingMethod(preference || DEFAULT_SHIPPING_METHOD);

  const shippingTotals: Totals = {
    __typename: 'Totals',
    express: makePrice(totals.express, currency),
    standard: makePrice(totals.standard, currency),
  };

  const estimatedStandard = makePriceWithTax({
    amountIncTax: standard,
    amountExTax: standardExTax,
    taxAmount: Math.round((standard - standardExTax) * 100) / 100,
    currency,
    type: 'shipping',
  });

  const estimatedExpress = allow_express
    ? makePriceWithTax({
        amountIncTax: express,
        amountExTax: expressExTax,
        taxAmount: Math.round((express - expressExTax) * 100) / 100,
        currency,
        type: 'shipping',
      })
    : null;

  const estimatedCost =
    allow_express && method === ShippingMethod.Express && estimatedExpress
      ? estimatedExpress
      : estimatedStandard;

  const estimated = {
    __typename: 'EstimatedShipping',
    express: estimatedExpress,
    standard: estimatedStandard,
  } satisfies EstimatedShipping;

  return {
    __typename: 'Shipping',
    totals: shippingTotals,
    estimated,
    countryCode,
    countryName,
    selected: {
      __typename: 'ShippingSelected',
      method,
      estimatedCost,
    },
  };
};

const transformSummary = (
  summary: MonolithSummary,
  {
    digital_products_only,
    count,
    currency,
  }: { digital_products_only: boolean; currency: string; count: number }
): Summary => {
  const {
    show_tax_separately,
    total,
    subtotal,
    subtotal_ex_tax,
    undiscounted_subtotal,
    undiscounted_subtotal_ex_tax,
    tax,
    estimated_shipping,
    totals,
    is_tax_computed,
  } = summary;

  const finalSubtotal = makePrice(subtotal, currency);
  const finalUndiscounted = makePrice(undiscounted_subtotal, currency);

  if (show_tax_separately) {
    finalSubtotal.amount = subtotal_ex_tax;
    finalUndiscounted.amount = undiscounted_subtotal_ex_tax;
  }

  return {
    __typename: 'Summary',
    digitalProductsOnly: digital_products_only,
    tax: tax ? makeTax(tax.amount, currency, tax.type) : null,
    showTaxSeparately: show_tax_separately,
    lineItemsCount: count,
    shipping: transformShipping(estimated_shipping, totals, currency),
    total: makePrice(total, currency),
    subtotal: finalSubtotal,
    undiscountedSubtotal: finalUndiscounted,
    isTaxComputed: is_tax_computed,
  };
};
